<template>
    <!-- eslint-disable vue/no-bare-strings-in-template -->
    <StickyContainer
        id="viewerToolbar"
        v-resize.immediate="onResize"
        :offset="topBarHeight - 1"
        :class="['bg-white border-slate-200 text-slate-600 border-t border-b']"
        @sticky="isAdSticky = $event"
    >
        <div class="menu">
            <div class="container menu-container h-full flex flex-row items-center text-sm">
                <div
                    :class="[
                        'flex flex-row items-center w-full justify-between',
                        { 'pointer-events-none disabled': actionsDisabled },
                    ]"
                >
                    <div class="flex flex-row items-stretch px-0 w-auto justify-start md:justify-center">
                        <div class="hidden lg:flex flex-row items-center justify-start md:justify-center">
                            <Btn
                                id="next"
                                variant="text"
                                :disabled="currentPage === viewerTotalPages"
                                :class="['pageDown', { 'protected-auth': actionsShouldPromptSignUp }]"
                                title="Next Page"
                                tabindex="14"
                                data-l10n-id="next"
                                icon="arrow-down"
                                compact
                                @click="pageNext()"
                            />
                            <Btn
                                id="previous"
                                variant="text"
                                :disabled="currentPage <= 1"
                                :class="['pageUp', { 'protected-auth': actionsShouldPromptSignUp }]"
                                title="Previous Page"
                                tabindex="13"
                                data-l10n-id="previous"
                                icon="arrow-up"
                                compact
                                @click="pagePrevious()"
                            />
                        </div>
                        <div
                            :class="[
                                'flex flex-row items-center border-r border-solid border-slate-200 px-2 text-xs md:text-base ',
                                actionsDisabled ? 'text-slate-300' : 'text-slate-600',
                            ]"
                        >
                            <input
                                id="pageNumber"
                                :value="currentPage"
                                data-testid="document-page-count"
                                :class="[
                                    'mr-2 my-2 px-1 text-center w-10 md:w-15',
                                    {
                                        'text-slate-400': actionsDisabled,
                                        'protected-auth': actionsShouldPromptSignUp,
                                    },
                                ]"
                                :readonly="actionsDisabled"
                                type="number"
                                size="4"
                                min="1"
                                tabindex="15"
                                @change="pageNumberChanged"
                            />
                            <div class="w-16 whitespace-nowrap">
                                {{ $t('show.toolbar.page_article') }}
                                <span class="ml-1">{{ viewerTotalPages }}</span>
                            </div>
                        </div>
                        <div
                            class="hidden md:flex flex-row items-center border-r border-solid border-slate-200 h-auto px-4"
                        >
                            <Btn
                                variant="text"
                                :disabled="actionsDisabled"
                                :class="['zoomOut', { 'protected-auth': actionsShouldPromptSignUp }]"
                                title="Zoom Out"
                                tabindex="21"
                                data-l10n-id="zoom_out"
                                icon="zoom-out"
                                compact
                                @click="$emit('zoom-out')"
                            />
                            <Btn
                                variant="text"
                                :disabled="actionsDisabled"
                                :class="['zoomIn', { 'protected-auth': actionsShouldPromptSignUp }]"
                                title="Zoom In"
                                tabindex="22"
                                data-l10n-id="zoom_in"
                                icon="zoom"
                                compact
                                @click="$emit('zoom-in')"
                            />
                        </div>
                        <div
                            :class="[
                                'flex flex-row items-center justify-center md:flex-grow lg:flex-grow-0 border-r border-solid border-slate-200 h-auto px-2 md:px-4 text-xs md:text-base leading-normal space-x-2',
                                { 'text-slate-400': actionsDisabled },
                            ]"
                        >
                            <ToggleSwitch
                                id="show-markings"
                                v-model="markingsVisible"
                                :disabled="actionsDisabled"
                                :class="['mr-3', { 'protected-auth': actionsShouldPromptSignUp }]"
                            >
                            </ToggleSwitch>
                            <span
                                class="hidden md:block"
                                style="min-width: 150px"
                            >
                                {{ $t('show.toolbar.label_show_markings') }}
                            </span>
                            <span
                                class="block md:hidden"
                                style="min-width: 40px"
                            >
                                {{ $t('show.toolbar.label_show_markings_sm') }}
                            </span>
                        </div>
                        <div
                            class="flex flex-row items-center border-r border-solid border-slate-200 h-auto pr-0 pl-2 md:px-5"
                        >
                            <Btn
                                id="rotate"
                                variant="text"
                                :disabled="actionsDisabled"
                                :class="['rotate', { 'protected-auth': actionsShouldPromptSignUp }]"
                                title="Rotate"
                                tabindex="15"
                                data-l10n-id="rotate"
                                icon="rotate-clockwise"
                                compact
                                @click="$emit('rotate')"
                            />
                        </div>
                    </div>

                    <div
                        id="findbar"
                        class="findbar flex flex-grow gap-2"
                    >
                        <div
                            v-if="user?.is_premium"
                            class="my-2 mx-2 lg:flex-shrink-0"
                        >
                            <BtnMenu
                                v-if="!document.infected && document.has_pdf"
                                :tooltip-text="$t('tooltip.download_document')"
                                variant="outlined"
                                :size="btnSize"
                                left-icon="downloads"
                                :label="getDownloadButtonLabelText(document.downloads)"
                                right-icon="chevron-down"
                                :options="downloadOptions"
                                data-specific-auth-trigger="download"
                                class="protected-auth w-full !p-0 whitespace-normal break-words"
                                menu-placement="bottom-end"
                                :loading="downloading"
                                :disabled="downloading"
                            />
                            <Btn
                                v-if="!document.infected && !document.has_pdf"
                                v-tooltip="{
                                    content: $t('tooltip.download_document'),
                                    disabled: document.infected,
                                }"
                                variant="outlined"
                                :size="btnSize"
                                data-specific-auth-trigger="download"
                                class="protected-auth w-full !p-0 whitespace-normal break-words"
                                :label="getDownloadButtonLabelText(document.downloads)"
                                left-icon="downloads"
                                :loading="downloading"
                                :disabled="downloading"
                                @click="download()"
                            />
                        </div>
                        <div
                            v-else
                            class="my-2 mx-2 lg:flex-shrink-0"
                        >
                            <BtnPremium
                                v-tooltip="{
                                    content: $t('tooltip.download_document'),
                                    disabled: document.infected,
                                }"
                                :label="getDownloadButtonLabelText(document.downloads)"
                                data-specific-auth-trigger="download"
                                left-icon="downloads"
                                :size="btnSize"
                                class="protected-auth w-full !p-0 whitespace-normal break-words"
                                :loading="downloading"
                                :disabled="downloading"
                                @click="download()"
                            />
                        </div>

                        <div class="search-input flex-grow">
                            <div class="relative my-2 max-w-md w-full">
                                <input
                                    id="findInput"
                                    :placeholder="$t('show.toolbar.placeholder_find_in_document')"
                                    tabindex="91"
                                    :class="['pr-12 py-2', { 'protected-auth': actionsShouldPromptSignUp }]"
                                    @keyup.enter="onSearch"
                                />
                                <span
                                    :class="[
                                        'absolute leading-none btn-search',
                                        actionsDisabled ? 'cursor-default' : 'cursor-pointer',
                                    ]"
                                    style="right: 1rem; top: 0.4rem"
                                >
                                    <i
                                        :class="[
                                            'icon icon-search w-7 h-7',
                                            {
                                                'text-slate-400': actionsDisabled,
                                                'protected-auth': actionsShouldPromptSignUp,
                                            },
                                        ]"
                                    ></i>
                                </span>
                            </div>
                            <div class="hidden">
                                <button
                                    id="findPrevious"
                                    class="findPrevious"
                                    title=""
                                    tabindex="92"
                                    data-l10n-id="find_previous"
                                >
                                    <span data-l10n-id="find_previous_label">Previous</span>
                                </button>
                                <button
                                    id="findNext"
                                    class="findNext"
                                    title=""
                                    tabindex="93"
                                    data-l10n-id="find_next"
                                >
                                    <span data-l10n-id="find_next_label">Next</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div id="headerLoadingBar">
                <div class="progress">
                    <div class="glimmer"></div>
                </div>
            </div>
        </div>
    </StickyContainer>
    <!-- eslint-disable vue/no-bare-strings-in-template -->
</template>

<script>
import { round } from 'lodash-es';
import { mapGetters } from 'vuex';
import ToggleSwitch from '@components/ToggleSwitch';
import StickyContainer from '@/components/StickyContainer';
import BtnPremium from '@components/BtnPremium/BtnPremium.vue';
import { showSubscriptionShop } from '@helpers/subscription.js';
import * as braze from '@braze/web-sdk';
import GtmMixin, { GtmEventName } from '@/mixins/gtmMixin';
import { defineAsyncComponent } from 'vue';
import route from '@router/route.js';
import { EventBus, EventBusEvent } from '@plugins/eventBus.js';

export default {
    name: 'DocumentPDFViewerToolbar',
    i18nOptions: {
        namespaces: ['document'],
    },
    components: {
        BtnPremium,
        ToggleSwitch,
        StickyContainer,
    },
    extends: GtmMixin,
    props: {
        /**
         * Current page of the document displayed
         */
        currentPage: {
            type: Number,
            default: 1,
        },
        showMarkings: {
            type: Boolean,
        },
        /**
         * Total pages calculated by the PDF viewer
         */
        viewerTotalPages: {
            type: Number,
            default: 0,
        },
    },
    emits: [
        'navigate-to-page',
        'rotate',
        'search',
        'search-next',
        'zoom-in',
        'zoom-out',
        'update:showMarkings',
    ],
    data() {
        return {
            getPdf: false,
            isAdSticky: false,
            downloading: false,
        };
    },
    computed: {
        ...mapGetters({
            document: 'document/currentDocument',
            isLoggedIn: 'auth/isLoggedIn',
            pdfViewerInitialized: 'document/pdfViewerInitialized',
            topBarHeight: 'ui/topBarHeight',
            user: 'auth/user',
        }),
        ...mapGetters('ui', ['screen', 'windowSize']),
        actionsDisabled() {
            return !this.pdfViewerInitialized;
        },
        actionsShouldPromptSignUp() {
            return !this.isLoggedIn;
        },
        markingsVisible: {
            get() {
                return this.showMarkings;
            },
            set(value) {
                this.$emit('update:showMarkings', value);
            },
        },
        downloadOptions() {
            return [
                {
                    icon: 'document',
                    label: this.$t('tooltip.download_original'),
                    onClick: () => {
                        this.getPdf = false;
                        this.download();
                    },
                },
                {
                    icon: 'file-pdf',
                    label: this.$t('tooltip.download_pdf'),
                    onClick: () => {
                        this.getPdf = true;
                        this.download(null);
                    },
                },
            ];
        },
        btnSize() {
            return this.screen.maxSM ? 'xs' : 'sm';
        },
    },
    methods: {
        getDownloadButtonLabelText(downloads) {
            return this.windowSize.width <= 480 ? null : this.$t('download.btn_text', { count: downloads });
        },
        onResize(el) {
            this.$store.commit('document/SET_TOOLBAR_HEIGHT', el.offsetHeight);
        },
        pagePrevious() {
            if (this.currentPage > 1) {
                this.$emit('navigate-to-page', this.currentPage - 1);
            }
        },
        pageNext() {
            if (this.currentPage < Number(this.viewerTotalPages)) {
                this.$emit('navigate-to-page', this.currentPage + 1);
            }
        },
        pageNumberChanged(event) {
            // Handle the user inputting a floating point number.
            const newPage = round(event.target.value);

            if (newPage < 1) {
                this.$emit('navigate-to-page', 1);
            } else if (newPage > this.viewerTotalPages) {
                this.$emit('navigate-to-page', this.viewerTotalPages);
            } else {
                this.$emit('navigate-to-page', newPage);
            }

            if (event.target.value !== newPage.toString()) {
                // eslint-disable-next-line no-param-reassign
                event.target.value = this.currentPage;
            }
        },
        onSearch(event) {
            const searchText = event.target.value;
            this.$emit('search', searchText);
        },
        download(recaptchaToken = null) {
            if (!this.document.user_is_uploader) {
                if (this.user?.is_premium_system_enabled && !this.user?.is_premium) {
                    showSubscriptionShop('document_download_button_click', this.document.id);
                    return;
                }
            }

            if (recaptchaToken === null && this.downloading) {
                return;
            }
            this.downloading = true;
            if (import.meta.env.MIX_BRAZE_SDK_ENABLED === 'true') {
                braze.logCustomEvent('FILE-DOWNLOADED', {
                    filename: this.document.filename,
                    course_name: this.document.course?.name,
                });
            }

            this.$store
                .dispatch('document/downloadDocument', {
                    recaptchaToken,
                    id: this.document.id,
                    fileName: this.document.filename,
                    getPdf: this.getPdf,
                })
                .then(() => {
                    this.pushGtmEvent(GtmEventName.MANUAL_DOWNLOAD_DOCUMENT);
                    if (this.shouldAutoJoinToCourse) {
                        this.joinUserToCourse();
                    } else {
                        this.$store.dispatch('flashMessages/showSuccess', {
                            content: this.$t('download.success'),
                            progress: { time: 3000 },
                        });
                    }

                    EventBus.emit(EventBusEvent.DOWNLOAD_DOCUMENT, this.isAdSticky);
                    this.downloading = false;
                })
                .catch(async (error) => {
                    if (error.response?.status === 403) {
                        // the user has reached the daily download limit and a
                        // captcha token is necessary to authorize the download
                        const token = await this.$modal.open(
                            defineAsyncComponent(() => import('@components/ModalCaptcha')),
                            {
                                context: 'download',
                            },
                        );

                        if (token) {
                            this.download(token);
                        } else {
                            this.downloading = false;
                        }
                        return;
                    }

                    if (error.response?.status === 429) {
                        await this.$modal.info({
                            title: this.$t('download.hard_limit'),
                            contentHtml: this.$t('download.hard_limit_text', {
                                link: route('landing-page.show', { slug: this.$t('common:footer.conditions_slug') }),
                                link_text: this.$t('common:footer.conditions'),
                            }),
                            submitText: this.$t('common:callout.finish'),
                        });

                        this.downloading = false;
                        return;
                    }

                    this.$store.dispatch('flashMessages/showError', {
                        content: this.$t(
                            error.response?.status === 401 || error.response?.status === 419
                                ? 'download.session'
                                : 'download.error',
                        ),
                        progress: { time: 3000 },
                    });
                    this.downloading = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
#viewerToolbar {
    z-index: 9;

    input {
        padding-bottom: 1px;
        padding-top: 1px;
    }
    &:not(.is-sticky) {
        @apply z-[8];
    }

    button {
        @apply flex px-2 py-1;
    }
}

#findInput[data-status='pending'] {
    background-image: none;
}

#findInput.notFound {
    @apply border-red-100;
}

#pageNumber::-webkit-outer-spin-button,
#pageNumber::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.search-input {
    @apply hidden;
    @media (min-width: 1200px) {
        @apply flex flex-row;
    }
}

/* Firefox */
input[type='number'] {
    -moz-appearance: textfield;
}
#headerLoadingBar {
    height: 3px;
    .progress {
        @apply bg-blue-600;
    }
}

@screen md {
    #viewerToolbar {
        input {
            padding-bottom: 0.25rem;
            padding-top: 0.25rem;
        }
    }
}
</style>
